import React from "react";
import styles from "./onboardingWelcomeStep.module.css";
import { tWithKeyAttr as t } from "../../i18nHelper";

interface OnboardingWelcomeStepProps {
  values: {
    email?: string;
    name?: string;
  };
  showSettingsCta?: boolean;
}

const OnboardingWelcomeStep: React.FC<OnboardingWelcomeStepProps> = ({
  values,
  showSettingsCta = false,
}) => {
  return (
    <div className={styles.welcomeContainer}>
      {/* Success Icon */}
      <div className={styles.successIcon}>
        <div className={styles.checkmark}>
          <svg
            width="50"
            height="50"
            viewBox="0 0 80 80"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <circle cx="40" cy="40" r="40" fill="#4CAF50" />
            <path
              d="M25 40L35 50L55 30"
              stroke="white"
              strokeWidth="4"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>
      </div>

      {/* Welcome Message */}
      <div className={styles.welcomeContent}>
        <h1 className={styles.welcomeTitle}>
          {t("ONBOARDING_WELCOME_TITLE") || "🎉 Welcome to BPreven!"}
        </h1>
        
        <div className={styles.successMessage}>
          <h2 className={styles.successTitle}>
            {t("ONBOARDING_SUCCESS_TITLE") || "Company Successfully Added!"}
          </h2>
          
          <p className={styles.successDescription}>
            {t("ONBOARDING_SUCCESS_DESCRIPTION") || 
              `Congratulations! Your company "${values.name || 'your company'}" has been successfully registered with BPreven.`}
          </p>
        </div>

        {/* Email Notification */}
        <div className={styles.emailNotification}>
          <div className={styles.emailIcon}>
            <svg
              width="32"
              height="32"
              viewBox="0 0 48 48"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <rect x="4" y="10" width="40" height="28" rx="4" fill="#6f42c1" />
              <path
                d="M4 14L24 26L44 14"
                stroke="white"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
          
          <div className={styles.emailContent}>
            <h3 className={styles.emailTitle}>
              {t("ONBOARDING_EMAIL_SENT_TITLE") || "Login Details Sent!"}
            </h3>
            <p className={styles.emailDescription}>
              {t("ONBOARDING_EMAIL_SENT_DESCRIPTION") || 
                `We've sent your login credentials to ${values.email || 'your email address'}. Please check your inbox and follow the instructions to access your account.`}
            </p>
          </div>
        </div>

        {/* Next Steps */}
        <div className={styles.nextSteps}>
          <h3 className={styles.nextStepsTitle}>
            {t("ONBOARDING_NEXT_STEPS_TITLE") || "What's Next?"}
          </h3>
          
          <div className={styles.stepsList}>
            <div className={styles.step}>
              <div className={styles.stepNumber}>1</div>
              <div className={styles.stepContent}>
                <h4 className={styles.stepTitle}>
                  {t("ONBOARDING_STEP_CHECK_EMAIL") || "Check Your Email"}
                </h4>
                <p className={styles.stepDescription}>
                  {t("ONBOARDING_STEP_CHECK_EMAIL_DESC") || "Look for our welcome email with your login credentials"}
                </p>
              </div>
            </div>
            
            <div className={styles.step}>
              <div className={styles.stepNumber}>2</div>
              <div className={styles.stepContent}>
                <h4 className={styles.stepTitle}>
                  {t("ONBOARDING_STEP_LOGIN") || "Login to Your Account"}
                </h4>
                <p className={styles.stepDescription}>
                  {t("ONBOARDING_STEP_LOGIN_DESC") || "Use the credentials to access your BPreven dashboard"}
                </p>
              </div>
            </div>
            
            <div className={styles.step}>
              <div className={styles.stepNumber}>3</div>
              <div className={styles.stepContent}>
                <h4 className={styles.stepTitle}>
                  {t("ONBOARDING_STEP_EXPLORE") || "Start Exploring"}
                </h4>
                <p className={styles.stepDescription}>
                  {t("ONBOARDING_STEP_EXPLORE_DESC") || "Discover all the powerful features BPreven has to offer"}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Support Information */}
        <div className={styles.supportInfo}>
          <p className={styles.supportText}>
            {t("ONBOARDING_SUPPORT_TEXT") || "Need help? Our support team is here for you 24/7."}
          </p>
          <div className={styles.supportContact}>
            <span className={styles.supportEmail}>
              {t("ONBOARDING_SUPPORT_EMAIL") || "support@bpreven.com"}
            </span>
          </div>
        </div>

        {/* Optional: company default settings CTA (shown only on success page) */}
        {showSettingsCta && (
          <div className={styles.settingsCta}>
            <p className={styles.settingsCtaNote}>
              {t("ONBOARDING_SETTINGS_CTA_NOTE") ||
                "כל ההגדרות אופציונליות — תוכלו לחזור בכל עת."}
            </p>
            <a
              href="/companySettings"
              className={styles.settingsCtaBtn}
            >
              {t("ONBOARDING_SETTINGS_CTA_LABEL") || "⚙️ הגדרות ברירת מחדל לחברה"}
            </a>
          </div>
        )}
      </div>

      {/* Decorative Elements */}
      <div className={styles.decorativeElements}>
        <div className={styles.floatingElement} style={{ top: '10%', left: '10%' }}>
          <div className={styles.circle}></div>
        </div>
        <div className={styles.floatingElement} style={{ top: '20%', right: '15%' }}>
          <div className={styles.triangle}></div>
        </div>
        <div className={styles.floatingElement} style={{ bottom: '15%', left: '5%' }}>
          <div className={styles.square}></div>
        </div>
        <div className={styles.floatingElement} style={{ bottom: '25%', right: '10%' }}>
          <div className={styles.diamond}></div>
        </div>
      </div>
    </div>
  );
};

export default React.memo(OnboardingWelcomeStep);
